<script lang="ts">
  import { fade } from "svelte/transition";
  import { createEventDispatcher, onMount } from "svelte";
  import api from "../../api";
  import { doctorStore } from "../../store";
  import type { Doctor } from "../../store";
  import Input from "../common/Input/Input.svelte";
  import Button from "../common/Button/Button.svelte";
  import IntroBackground from "../Background/IntroBackground.svelte";
  import CountryCodeSelect from "../common/CountryCodeSelect/CountryCodeSelect.svelte";
  import { getHandoffURL, getOfferURL } from "../../handoff";
  import { i18n, language } from "../../i18n";

  // What the site accepts for `phone` once it has stripped the separators, per
  // `docs/registration-handoff.md` of developer/new-express-doctor. A number it
  // rejects dead-ends the patient on a bare-text 400 with the widget gone, so
  // the same rule has to be applied here, where the field is still on screen.
  const PHONE_PATTERN = /^\+?\d{7,15}$/;

  const dispatch = createEventDispatcher();
  let name = "";
  let phone = "";
  let acceptTerms = false;
  let showTermsError = false;
  let showPhoneError = false;
  let countryPhonePrefix = getFallbackPhonePrefix();
  let doctor: Doctor | null = null;
  export let hasIntroAnimationBeenRun;

  doctorStore.subscribe((value) => {
    doctor = value;
  });

  // `/v4/region` answers null whenever its geo lookup produced nothing, so the
  // widget needs a default of its own; the RU tunnel implies its own answer.
  function getFallbackPhonePrefix() {
    return window.location.hostname.startsWith(__RU_SITE_PREFIX__)
      ? "+7"
      : "+972";
  }

  onMount(async () => {
    try {
      const prefix = await api.getPhonePrefix();
      // Retried, the lookup can answer long after the card is on screen, and
      // it only ever supplies a default: a country picked meanwhile outranks it.
      if (prefix && countryPhonePrefix === getFallbackPhonePrefix()) {
        countryPhonePrefix = prefix;
      }
    } catch (error) {
      // A missing default is not worth failing the form over.
    }
  });

  function getParsedNumber(number: string) {
    return number.replace(/[+()\- ]/g, "");
  }

  function handleSubmit(event: Event) {
    if (!isPhoneValid) {
      event.preventDefault();
      showPhoneError = true;
    }
    if (!acceptTerms) {
      event.preventDefault();
      showTermsError = true;
    }
  }

  $: if (acceptTerms) {
    showTermsError = false;
  }
  $: if (isPhoneValid) {
    showPhoneError = false;
  }
  $: animationConfig = hasIntroAnimationBeenRun
    ? {
        HEADER_FADE_IN_DURATION: 0,
        FLY_IN_DURATION: 0,
        FORM_FADE_IN_DURATION: 0,
      }
    : {
        HEADER_FADE_IN_DURATION: 600,
        FLY_IN_DURATION: 1000,
        FORM_FADE_IN_DURATION: 300,
      };
  $: fullPhoneNumber = countryPhonePrefix + getParsedNumber(phone);
  $: isPhoneValid = PHONE_PATTERN.test(fullPhoneNumber);
  $: isButtonDisabled = !name || getParsedNumber(phone).length === 0;
  $: isReady = doctor !== null;
  $: handoffURL = getHandoffURL($language, api.getDoctorId());
  $: offerURL = getOfferURL($language);
</script>

<div class="Form__container">
  {#if isReady}
    <IntroBackground {...animationConfig} {doctor} />
    <!--
      The handoff rides a cookie the main site sets on itself: `target="_top"`
      breaks out of the widget's iframe, where `express.doctor` would be a third
      party and the `Set-Cookie` partitioned away. `accept-charset` keeps
      Cyrillic names from arriving as mojibake.
    -->
    <form
      method="post"
      accept-charset="utf-8"
      target="_top"
      action={handoffURL}
      on:submit={handleSubmit}
      in:fade={{
        delay:
          animationConfig.HEADER_FADE_IN_DURATION +
          3 * animationConfig.FLY_IN_DURATION,
        duration: animationConfig.FORM_FADE_IN_DURATION,
      }}
      on:introend={() => dispatch("animationBeenRun")}
      class="Form"
    >
      <label for="full_name">{$i18n.t("form_fio")}</label>
      <Input
        id="full_name"
        name="full_name"
        maxlength="200"
        placeholder={$i18n.t("form_phone_placeholder")}
        bind:value={name}
      />
      <label>{$i18n.t("form_phone_title")}</label>
      <CountryCodeSelect bind:phone bind:countryPhonePrefix />
      <input type="hidden" name="phone" value={fullPhoneNumber} />
      {#if showPhoneError}
        <div class="Form__error">{$i18n.t("form_phone_invalid")}</div>
      {/if}
      <div class="Form__terms">
        <input
          id="accept_terms"
          type="checkbox"
          name="accept_terms"
          bind:checked={acceptTerms}
        />
        <label for="accept_terms">
          {@html $i18n.t("form_terms_offer", { offerUrl: offerURL })}
        </label>
      </div>
      {#if showTermsError}
        <div class="Form__error">
          {$i18n.t("form_terms_offer_required")}
        </div>
      {/if}
      <Button class="Form__button" disabled={isButtonDisabled}>
        {$i18n.t("form_button_submit")}
      </Button>
    </form>
  {/if}
</div>

<style>
  label {
    width: 100%;
    font-style: normal;
    font-weight: bold;
    font-size: 14px;
    line-height: 24px;
    text-align: left;
  }

  .Form__container {
    background-color: var(--silver);
    border-radius: inherit;
    height: 100%;
    width: 100%;
  }

  .Form {
    align-items: center;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 16px;
    width: 100%;
    background-color: #fff;
    border-radius: 16px;
  }

  .Form__terms {
    margin-top: 20px;
    display: flex;
    align-items: flex-start;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;
    text-align: left;
    width: 100%;
    color: var(--darkGrey);
  }

  .Form__terms input {
    margin: 0 8px 0 0;
    flex-shrink: 0;
  }

  .Form__terms label {
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;
    width: auto;
  }

  .Form__terms :global(a) {
    color: var(--primary);
    text-decoration: none;
  }

  .Form__error {
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
    text-align: left;
    width: 100%;
    color: var(--danger);
  }
</style>
